import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Progress } from '@/components/ui/progress';
import { useLiquidationRiskByProtocol, getRiskLevel, getRiskBadgeVariant } from '@/hooks/useLiquidationRisk';
import { formatUSD, formatNum } from '@/lib/format';
import { AlertTriangle, Activity, Info, TrendingUp, Flame, ShieldAlert } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface LiquidationRiskCardProps {
  protocol: 'aave-v3' | 'compound-v3';
}

const protocolNames: Record<string, string> = {
  'aave-v3': 'Aave V3',
  'compound-v3': 'Compound V3'
};

const protocolColors: Record<string, string> = {
  'aave-v3': 'text-purple-500',
  'compound-v3': 'text-green-500'
};

export const LiquidationRiskCard = ({ protocol }: LiquidationRiskCardProps) => {
  const { data, isLoading, isError } = useLiquidationRiskByProtocol(protocol);

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <Skeleton className="h-5 w-32" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-24" />
        </CardContent>
      </Card>
    );
  }

  if (isError || !data) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Activity className="h-4 w-4" />
            {protocolNames[protocol]} Risk
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No data available</p>
        </CardContent>
      </Card>
    );
  }

  const riskLevel = getRiskLevel(data.utilization_rate);
  const badgeVariant = getRiskBadgeVariant(riskLevel);

  const utilizationColor = 
    data.utilization_rate >= 90 ? 'bg-red-500' :
    data.utilization_rate >= 80 ? 'bg-orange-500' :
    data.utilization_rate >= 70 ? 'bg-yellow-500' : 'bg-green-500';

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <ShieldAlert className={`h-4 w-4 ${protocolColors[protocol]}`} />
            {protocolNames[protocol]}
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-3 w-3 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-xs">
                  Monitors DeFi protocol health metrics.
                  <br /><br />
                  <strong>Utilization Rate</strong>: % of supplied assets being borrowed. High utilization (&gt;85%) indicates systemic risk.
                  <br />
                  <strong>Liquidations</strong>: Forced sales that can cause cascading price drops.
                </p>
              </TooltipContent>
            </Tooltip>
          </CardTitle>
          <Badge variant={badgeVariant} className="text-xs uppercase">
            {riskLevel} risk
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Utilization Rate - Main Metric */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Utilization Rate</span>
            <span className={`text-lg font-bold ${
              data.utilization_rate >= 85 ? 'text-red-500' : 
              data.utilization_rate >= 70 ? 'text-yellow-500' : 'text-green-500'
            }`}>
              {formatNum(data.utilization_rate)}%
            </span>
          </div>
          <Progress 
            value={Math.min(data.utilization_rate, 100)} 
            className="h-2"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Safe</span>
            <span className="text-yellow-500">Warning</span>
            <span className="text-red-500">Critical</span>
          </div>
        </div>

        {/* TVL Info */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground text-xs">Total Supplied</span>
            <p className="font-medium">{formatUSD(data.total_supplied_usd)}</p>
          </div>
          <div>
            <span className="text-muted-foreground text-xs">Total Borrowed</span>
            <p className="font-medium">{formatUSD(data.total_borrowed_usd)}</p>
          </div>
        </div>

        {/* Liquidations */}
        <div className="border-t pt-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Flame className="h-3 w-3 text-orange-500" />
              Liquidations 24h
            </div>
            <div className="text-right">
              <span className={`font-medium ${
                data.liquidations_24h_usd > 5000000 ? 'text-red-500' : 'text-foreground'
              }`}>
                {formatUSD(data.liquidations_24h_usd)}
              </span>
              <span className="text-xs text-muted-foreground ml-2">
                ({data.liquidations_24h_count} events)
              </span>
            </div>
          </div>
        </div>

        {/* At Risk Positions */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <AlertTriangle className="h-3 w-3 text-yellow-500" />
            At Risk Positions
          </div>
          <div className="text-right">
            <span className={`font-medium ${
              data.at_risk_positions_usd > 10000000 ? 'text-yellow-500' : 'text-foreground'
            }`}>
              {formatUSD(data.at_risk_positions_usd)}
            </span>
            <span className="text-xs text-muted-foreground ml-2">
              ({data.at_risk_positions_count} positions)
            </span>
          </div>
        </div>

        {/* Health Factor */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Avg Health Factor</span>
          <span className={`font-medium ${
            data.health_factor_avg < 1.2 ? 'text-red-500' : 
            data.health_factor_avg < 1.5 ? 'text-yellow-500' : 'text-green-500'
          }`}>
            {formatNum(data.health_factor_avg, 2)}
          </span>
        </div>

        {/* Top At Risk Positions */}
        {data.top_at_risk_positions && data.top_at_risk_positions.length > 0 && (
          <div className="border-t pt-3">
            <span className="text-xs text-muted-foreground">Top At-Risk Positions</span>
            <div className="mt-2 space-y-2">
              {data.top_at_risk_positions.slice(0, 3).map((pos: any, idx: number) => (
                <div key={idx} className="flex items-center justify-between text-xs">
                  <span className="font-mono truncate max-w-[100px]">
                    {pos.address?.slice(0, 6)}...{pos.address?.slice(-4)}
                  </span>
                  <span className="text-muted-foreground">
                    HF: <span className={pos.health_factor < 1.15 ? 'text-red-500' : 'text-yellow-500'}>
                      {formatNum(pos.health_factor, 2)}
                    </span>
                  </span>
                  <span>{formatUSD(pos.debt_usd)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Data source */}
        <div className="pt-2 text-xs text-muted-foreground text-center">
          Source: The Graph • Updated hourly
        </div>
      </CardContent>
    </Card>
  );
};
